import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PageHeader } from '@/components/efs/PageHeader';
import { cn } from '@/lib/utils';
import { formatBatchFacility, formatBatchMonth, formatDateTime } from '@/lib/format';
import { apiClient, type ApiSuccess } from '@/lib/api-client';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import type { ApiClientError } from '@/lib/api-client';
import {
  AuditTargetLink,
  LocalizedApiError,
  QueryFailureNotice,
  TARGET_RECORD_TYPES,
  type IssuePresentation,
} from '@/features/audit-issues';

import {
  useApplyCorrectionRequest,
  useApproveCorrectionRequest,
  useCorrectionRequests,
  useCorrectionSchema,
  useCorrectionTargets,
  useCreateCorrectionRequest,
  useRejectCorrectionRequest,
} from './useCorrections';
import {
  correctionStatusValues,
  type CorrectionFieldSpec,
  type CorrectionFieldChange,
  type CorrectionRequestDto,
  type CorrectionStatus,
  type CorrectionTypeSpec,
} from './types';

const NS = 'corrections';

/** Minimal `t` signature — matches the rest of the shared spec-13 modules. */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The 10 correctable entities (spec-13 §8.8 round 2). Mirrors
 * `backend/src/modules/correction_requests/model.rs::CORRECTABLE_TYPES`.
 * Labels live under `efs.corrections.entity_labels.*` (this namespace file).
 */
const CORRECTION_ENTITIES = [
  'first_valuation',
  'weight_difference_check',
  'monthly_material_valuation',
  'getit_purchase_price',
  'ef_dk_settlement',
  'sales_outbound',
  'reclassified_item_receipt',
  'inbound_package',
  'inbound_lot',
  'monthly_batch',
] as const;
type CorrectionEntity = (typeof CORRECTION_ENTITIES)[number];
const isCorrectionEntity = (v: string): v is CorrectionEntity =>
  (CORRECTION_ENTITIES as readonly string[]).includes(v);

/** Every field key any correctable entity may carry — drives the visible
 * change summary; keys outside this set stay in the technical disclosure
 * only (spec-13 §8.8 round 2 B). */
const KNOWN_FIELD_KEYS: ReadonlySet<string> = new Set([
  'final_value',
  'final_reason',
  'reason',
  'recompute',
  'sales_date',
  'vendor_type',
  'outbound_weight',
  'unit_price',
  'weight',
  'gross_kg',
  'tare_kg',
  'notes',
  'total_kg',
  'delay_reason',
  'opening_balance_note',
]);

/** Historical rows may carry any catalog record type, not just the new schema entities. */
const isKnownRecordType = (type: string): boolean =>
  (TARGET_RECORD_TYPES as readonly string[]).includes(type);

/** Localized entity/record-type label for the list target cell. Falls back
 * to the raw slug (rendered in `<code>` by the caller) for unknown types. */
const entityLabel = (t: TFn, type: string): string | null => {
  if (isCorrectionEntity(type)) return t(`efs.corrections.entity_labels.${type}`);
  if (isKnownRecordType(type)) return t(`efs.issues.record_types.${type}`, { ns: 'issues' });
  return null;
};

/**
 * Present a correction row's target through the shared audit-issue contract
 * so `AuditTargetLink` applies the allowlisted resolver, route-access check,
 * and unavailable/permission copy (round-6 item 6). No UUID reaches the label.
 */
const correctionTargetPresentation = (r: CorrectionRequestDto): IssuePresentation => ({
  issue_code: 'correction_request',
  message_origin: 'manual',
  severity: 'info',
  can_open_target: r.can_open_target === true && !!r.navigation,
  target: {
    monthly_batch_id: r.monthly_batch_id,
    batch_display: r.batch_display,
    record_type: r.target_entity_type,
    record_id: null,
    record_code: r.target_label,
    navigation: r.navigation ?? null,
  },
});

const canViewTechnicalCorrections = (user: ReturnType<typeof useCurrentUser>): boolean =>
  hasPermission(user, 'correction.approve') ||
  hasPermission(user, 'estimation.final_decide') ||
  hasPermission(user, 'system.admin');

const FINANCIAL_CORRECTION_ENTITIES: ReadonlySet<string> = new Set([
  'first_valuation',
  'monthly_material_valuation',
  'getit_purchase_price',
  'ef_dk_settlement',
]);

const requiredReviewPermission = (entity: string): string =>
  FINANCIAL_CORRECTION_ENTITIES.has(entity) ? 'estimation.final_decide' : 'correction.approve';

const canReviewCorrection = (user: ReturnType<typeof useCurrentUser>, entity: string): boolean =>
  hasPermission(user, requiredReviewPermission(entity));

const canViewTechnicalCorrection = (
  user: ReturnType<typeof useCurrentUser>,
  entity: string,
): boolean => hasPermission(user, 'system.admin') || canReviewCorrection(user, entity);

const statusToneClass: Record<CorrectionStatus, string> = {
  requested: 'bg-blue-50 text-blue-900',
  approved: 'bg-amber-50 text-amber-900',
  rejected: 'bg-rose-50 text-rose-700',
  applied: 'bg-emerald-50 text-emerald-900',
  voided: 'bg-efs-bg-subtle text-efs-text-muted',
};

const StatusBadge = ({ status }: { status: CorrectionStatus }) => {
  const { t } = useTranslation(NS);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
        statusToneClass[status],
      )}
    >
      {t(`efs.governance.corrections.status.${status}`, { defaultValue: status })}
    </span>
  );
};

/**
 * Flat, localized `{field label: value}` rows for the requested-change
 * summary (spec-13 §8.8 round 2 B). Only keys in `KNOWN_FIELD_KEYS` render
 * here; every key (known or not) stays available in the raw technical
 * disclosure below.
 */
const ChangeFieldRows = ({ value }: { value: unknown }) => {
  const { t } = useTranslation(NS);
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value as Record<string, unknown>).filter(([field]) =>
    KNOWN_FIELD_KEYS.has(field),
  );
  if (entries.length === 0) return null;
  return (
    <ul className="space-y-0.5">
      {entries.map(([field, v]) => (
        <li key={field} className="text-xs text-efs-text-muted">
          <span className="text-efs-text-primary">
            {t(`efs.corrections.field_labels.${field}`)}
          </span>
          {': '}
          {v === null ? '—' : typeof v === 'string' ? v : JSON.stringify(v)}
        </li>
      ))}
    </ul>
  );
};

const ChangeRowsFromFields = ({ changes }: { changes: CorrectionFieldChange[] }) => {
  const { t } = useTranslation(NS);
  if (changes.length === 0) return null;
  return (
    <ul className="space-y-1">
      {changes.map((change) => (
        <li key={change.field} className="text-xs text-efs-text-muted">
          <span className="text-efs-text-primary">
            {t(`efs.corrections.field_labels.${change.field}`)}
          </span>
          <span>{': '}</span>
          <span>{formatChangeValue(change.before)}</span>
          <span aria-hidden> → </span>
          <span>{formatChangeValue(change.after)}</span>
        </li>
      ))}
    </ul>
  );
};

const formatChangeValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '-';
};

interface MonthlyBatchOption {
  monthly_batch_id: string;
  year_month: string;
  facility_name?: string | null;
  company_display_name?: string | null;
}

/** Monthly-batch picker options, reusing the existing list endpoint
 * (frontend/src/features/monthly-batches/MonthlyBatchesPage.tsx). */
const useMonthlyBatchOptions = () =>
  useQuery<MonthlyBatchOption[], ApiClientError>({
    queryKey: ['monthly-batches', 'list', { page: 1, per_page: 100 }],
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<MonthlyBatchOption[]>>('/monthly-batches', {
        params: { page: 1, per_page: 100 },
      });
      return res.data.data ?? [];
    },
  });

/** Per-field editor state. Which sub-field is read depends on the field kind. */
interface FieldState {
  /** number / text / date raw input. */
  value: string;
  /** Clearable text fields: explicit "send null" toggle. */
  clear: boolean;
  /** Flag fields: checkbox state. */
  flag: boolean;
}

const EMPTY_FIELD_STATE: FieldState = { value: '', clear: false, flag: false };

/** Build the structured `new_value` payload from the editor state — only
 * fields the operator actually touched are included (spec-13 §8.8 round 2). */
const buildNewValue = (
  fields: CorrectionFieldSpec[],
  state: Record<string, FieldState>,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const s = state[field.key] ?? EMPTY_FIELD_STATE;
    if (field.kind === 'flag') {
      if (s.flag) out[field.key] = true;
      continue;
    }
    if (field.kind === 'text' && field.clearable && s.clear) {
      out[field.key] = null;
      continue;
    }
    const trimmed = s.value.trim();
    if (trimmed === '') continue;
    out[field.key] = trimmed;
  }
  return out;
};

interface CreateSubmitPayload {
  entity: string;
  monthlyBatchId: string;
  targetEntityId: string;
  reason: string;
  newValue: Record<string, unknown>;
}

interface CreateForm {
  entity: string;
  monthlyBatchId: string;
  targetEntityId: string;
  reason: string;
  fields: Record<string, FieldState>;
}

const EMPTY_FORM: CreateForm = {
  entity: '',
  monthlyBatchId: '',
  targetEntityId: '',
  reason: '',
  fields: {},
};

const FieldEditor = ({
  spec,
  state,
  onChange,
}: {
  spec: CorrectionFieldSpec;
  state: FieldState;
  onChange: (next: FieldState) => void;
}) => {
  const { t } = useTranslation(NS);
  const label = t(`efs.corrections.field_labels.${spec.key}`);

  if (spec.kind === 'flag') {
    return (
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.flag}
            onChange={(e) => onChange({ ...state, flag: e.target.checked })}
          />
          {label}
        </label>
        <p className="text-xs text-efs-text-muted">
          {t('efs.governance.corrections.create.recompute_hint')}
        </p>
      </div>
    );
  }

  if (spec.kind === 'date') {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <DatePicker value={state.value} onChange={(v) => onChange({ ...state, value: v })} />
      </div>
    );
  }

  if (spec.kind === 'text') {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <Textarea
          value={state.value}
          disabled={spec.clearable && state.clear}
          onChange={(e) => onChange({ ...state, value: e.target.value })}
          rows={2}
        />
        {spec.clearable ? (
          <label className="flex items-center gap-2 text-xs text-efs-text-muted">
            <input
              type="checkbox"
              checked={state.clear}
              onChange={(e) =>
                onChange({
                  ...state,
                  clear: e.target.checked,
                  value: e.target.checked ? '' : state.value,
                })
              }
            />
            {t('efs.governance.corrections.create.clear_value')}
          </label>
        ) : null}
      </div>
    );
  }

  // number
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        inputMode="decimal"
        value={state.value}
        onChange={(e) => onChange({ ...state, value: e.target.value })}
      />
    </div>
  );
};

const CreateDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  error,
  showTechnicalDetails,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (form: CreateSubmitPayload) => void;
  isPending: boolean;
  error: ApiClientError | null;
  showTechnicalDetails: boolean;
}) => {
  const { t } = useTranslation(NS);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);

  const schemaQuery = useCorrectionSchema();
  const batchesQuery = useMonthlyBatchOptions();
  const targetsQuery = useCorrectionTargets(form.entity, form.monthlyBatchId);

  const schema = schemaQuery.data ?? [];
  const batches = batchesQuery.data ?? [];
  const targets = targetsQuery.data ?? [];

  const currentSpec: CorrectionTypeSpec | undefined = schema.find((s) => s.entity === form.entity);

  const update = (patch: Partial<CreateForm>) => setForm((prev) => ({ ...prev, ...patch }));

  const setEntity = (entity: string) =>
    setForm((prev) => ({ ...prev, entity, targetEntityId: '', fields: {} }));

  const setMonthlyBatchId = (monthlyBatchId: string) =>
    setForm((prev) => ({ ...prev, monthlyBatchId, targetEntityId: '' }));

  const setFieldState = (key: string, next: FieldState) =>
    setForm((prev) => ({ ...prev, fields: { ...prev.fields, [key]: next } }));

  const newValue = useMemo(
    () => (currentSpec ? buildNewValue(currentSpec.fields, form.fields) : {}),
    [currentSpec, form.fields],
  );

  const canSubmit =
    form.entity.trim() !== '' &&
    form.monthlyBatchId.trim() !== '' &&
    form.targetEntityId.trim() !== '' &&
    form.reason.trim() !== '' &&
    Object.keys(newValue).length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      entity: form.entity,
      monthlyBatchId: form.monthlyBatchId,
      targetEntityId: form.targetEntityId,
      reason: form.reason.trim(),
      newValue,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setForm(EMPTY_FORM);
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('efs.governance.corrections.create.title', { defaultValue: '修正依頼の作成' })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.governance.corrections.create.subtitle', {
              defaultValue: 'ロック済みデータの変更を依頼します。承認後に適用されます。',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cr-entity">{t('efs.governance.corrections.create.entity_label')}</Label>
            {schemaQuery.isLoading ? (
              <p className="text-xs text-efs-text-muted">
                {t('efs.governance.corrections.create.schema_loading')}
              </p>
            ) : null}
            {schemaQuery.isError ? (
              <div
                role="alert"
                className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs"
              >
                <p>{t('efs.governance.corrections.create.schema_failed')}</p>
                <Button size="sm" variant="outline" onClick={() => void schemaQuery.refetch()}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {t('efs.issues.states.retry', { ns: 'issues' })}
                </Button>
              </div>
            ) : null}
            <Select value={form.entity} onValueChange={setEntity}>
              <SelectTrigger id="cr-entity">
                <SelectValue
                  placeholder={t('efs.governance.corrections.create.entity_placeholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {schema.map((spec) => (
                  <SelectItem key={spec.entity} value={spec.entity}>
                    {t(`efs.corrections.entity_labels.${spec.entity}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!schemaQuery.isLoading && !schemaQuery.isError && schema.length === 0 ? (
              <p className="text-xs text-efs-text-muted">
                {t('efs.governance.corrections.create.schema_empty')}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-batch">{t('efs.issues.target.batch_label', { ns: 'issues' })}</Label>
            {batchesQuery.isError ? (
              <div
                role="alert"
                className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs"
              >
                <p>
                  {batchesQuery.error?.status === 403
                    ? t('efs.governance.corrections.create.batch_forbidden')
                    : t('efs.governance.corrections.create.batch_failed')}
                </p>
                <Button size="sm" variant="outline" onClick={() => void batchesQuery.refetch()}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {t('efs.issues.states.retry', { ns: 'issues' })}
                </Button>
              </div>
            ) : null}
            <Select value={form.monthlyBatchId} onValueChange={setMonthlyBatchId}>
              <SelectTrigger id="cr-batch">
                <SelectValue placeholder={t('efs.issues.target.batch_label', { ns: 'issues' })} />
              </SelectTrigger>
              <SelectContent>
                {batches.map((b) => (
                  <SelectItem key={b.monthly_batch_id} value={b.monthly_batch_id}>
                    {formatBatchMonth(b.year_month)}
                    {b.facility_name ? ` / ${formatBatchFacility(b)}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {batchesQuery.isLoading ? (
              <p className="text-xs text-efs-text-muted">
                {t('efs.governance.corrections.create.batch_loading')}
              </p>
            ) : null}
            {!batchesQuery.isLoading && !batchesQuery.isError && batches.length === 0 ? (
              <p className="text-xs text-efs-text-muted">
                {t('efs.governance.corrections.create.batch_empty')}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-target">{t('efs.governance.corrections.create.target_label')}</Label>
            <Select
              value={form.targetEntityId}
              onValueChange={(v) => update({ targetEntityId: v })}
              disabled={
                form.entity.trim() === '' ||
                form.monthlyBatchId.trim() === '' ||
                targetsQuery.isLoading ||
                targetsQuery.isError
              }
            >
              <SelectTrigger id="cr-target">
                <SelectValue
                  placeholder={
                    form.entity.trim() === '' || form.monthlyBatchId.trim() === ''
                      ? t('efs.governance.corrections.create.target_select_batch_first')
                      : t('efs.governance.corrections.create.target_placeholder')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {targets.map((opt) => (
                  <SelectItem key={opt.target_entity_id} value={opt.target_entity_id}>
                    {opt.status ? `${opt.label} (${opt.status})` : opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {targetsQuery.isLoading ? (
              <p className="text-xs text-efs-text-muted">
                {t('efs.governance.corrections.create.target_loading')}
              </p>
            ) : null}
            {targetsQuery.isError ? (
              <div
                role="alert"
                className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs"
              >
                <p>
                  {targetsQuery.error?.status === 403
                    ? t('efs.governance.corrections.create.target_forbidden')
                    : t('efs.governance.corrections.create.target_failed')}
                </p>
                <Button size="sm" variant="outline" onClick={() => void targetsQuery.refetch()}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {t('efs.issues.states.retry', { ns: 'issues' })}
                </Button>
              </div>
            ) : null}
            {form.entity.trim() !== '' &&
            form.monthlyBatchId.trim() !== '' &&
            !targetsQuery.isLoading &&
            !targetsQuery.isError &&
            targets.length === 0 ? (
              <p className="text-xs text-efs-text-muted">
                {t('efs.governance.corrections.create.target_empty')}
              </p>
            ) : null}
          </div>

          {currentSpec ? (
            <div className="space-y-3 rounded-md border border-efs-border p-3">
              <p className="text-xs font-medium text-efs-text-muted">
                {t('efs.governance.corrections.create.fields_heading')}
              </p>
              {currentSpec.fields.map((field) => (
                <FieldEditor
                  key={field.key}
                  spec={field}
                  state={form.fields[field.key] ?? EMPTY_FIELD_STATE}
                  onChange={(next) => setFieldState(field.key, next)}
                />
              ))}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="cr-reason">
              {t('efs.governance.corrections.fields.reason', { defaultValue: '理由' })}
            </Label>
            <Textarea
              id="cr-reason"
              value={form.reason}
              onChange={(e) => update({ reason: e.target.value })}
              placeholder={t('efs.governance.corrections.create.reason_placeholder', {
                defaultValue: '修正が必要な理由を入力してください',
              })}
              rows={3}
            />
          </div>

          {error ? (
            <LocalizedApiError error={error} showTechnicalDetails={showTechnicalDetails} />
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending || !canSubmit}>
            {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.governance.corrections.actions.create', { defaultValue: '依頼する' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CorrectionRequestsPage = () => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();

  const canRequest = hasPermission(user, 'correction.request');
  const showCreateTechnicalDetails = canViewTechnicalCorrections(user);

  const [statusFilter, setStatusFilter] = useState<CorrectionStatus | 'all'>('all');
  const [createOpen, setCreateOpen] = useState(false);

  const query = useCorrectionRequests(statusFilter === 'all' ? {} : { status: statusFilter });
  const create = useCreateCorrectionRequest();
  const approve = useApproveCorrectionRequest();
  const reject = useRejectCorrectionRequest();
  const apply = useApplyCorrectionRequest();

  const rows = query.data ?? [];

  const actionError: ApiClientError | null = approve.error ?? reject.error ?? apply.error ?? null;
  const actionErrorTargetId = approve.error
    ? approve.variables
    : reject.error
      ? reject.variables
      : apply.error
        ? apply.variables
        : undefined;
  const actionErrorRow = actionErrorTargetId
    ? rows.find((row) => row.id === actionErrorTargetId)
    : undefined;
  const showActionTechnicalDetails = actionErrorRow
    ? canViewTechnicalCorrection(user, actionErrorRow.target_entity_type)
    : hasPermission(user, 'system.admin');

  const handleCreate = async (form: CreateSubmitPayload) => {
    try {
      await create.mutateAsync({
        monthly_batch_id: form.monthlyBatchId,
        target_entity_type: form.entity,
        target_entity_id: form.targetEntityId,
        new_value: form.newValue,
        reason: form.reason,
      });
      setCreateOpen(false);
    } catch {
      // Surfaced via `create.error` in the dialog; keep it open for a retry.
    }
  };

  const columns: MasterTableColumn<CorrectionRequestDto>[] = useMemo(
    () => [
      {
        key: 'status',
        header: t('efs.governance.corrections.columns.status', { defaultValue: '状態' }),
        cell: (r) => <StatusBadge status={r.status} />,
      },
      {
        key: 'target',
        header: t('efs.governance.corrections.columns.target', { defaultValue: '対象' }),
        cell: (r) => {
          const label =
            entityLabel(t, r.target_entity_type) ??
            t('efs.governance.corrections.fields.unknown_target_type');
          return (
            <div className="min-w-0">
              <div className="text-sm text-efs-text-primary">{label}</div>
              {r.target_label ? (
                <div className="text-xs text-efs-text-muted">{r.target_label}</div>
              ) : null}
              {r.batch_display ? (
                <div className="text-xs text-efs-text-muted">{r.batch_display}</div>
              ) : null}
              {/* Round-6 item 6: permission-aware allowlisted target action; the
                  shared link renders the localized unavailable / permission
                  state (plus the authorized batch fallback) when it cannot open. */}
              <div className="mt-1">
                <AuditTargetLink
                  presentation={correctionTargetPresentation(r)}
                  actionLabel={t('efs.governance.corrections.actions.open_target')}
                />
              </div>
              {canViewTechnicalCorrection(user, r.target_entity_type) && r.target_entity_id ? (
                <details className="mt-0.5">
                  <summary className="cursor-pointer text-[11px] text-efs-text-muted hover:text-efs-text-primary">
                    {t('efs.issues.target.technical_details', { ns: 'issues' })}
                  </summary>
                  <p className="mt-0.5 break-all font-mono text-[11px] text-efs-text-muted">
                    {r.target_entity_type}: {r.target_entity_id}
                  </p>
                </details>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'reason',
        header: t('efs.governance.corrections.columns.reason', { defaultValue: '理由' }),
        cell: (r) => <span className="text-sm">{r.reason}</span>,
      },
      {
        key: 'requested_change',
        header: t('efs.governance.corrections.columns.requested_change', {
          defaultValue: '変更内容',
        }),
        cell: (r) => {
          const fieldChanges = r.field_changes ?? [];
          const showRowTechnicalDetails = canViewTechnicalCorrection(user, r.target_entity_type);
          const hasFieldChanges = fieldChanges.length > 0;
          const hasBeforeAfter = r.old_value != null || r.new_value != null;
          const hasAnyChange = hasBeforeAfter || r.requested_change != null;
          return (
            <div className="min-w-0 max-w-xs space-y-1">
              {hasFieldChanges ? (
                <ChangeRowsFromFields changes={fieldChanges} />
              ) : hasBeforeAfter && showRowTechnicalDetails ? (
                <>
                  {r.old_value != null ? (
                    <div>
                      <p className="text-[11px] font-medium text-efs-text-muted">
                        {t('efs.governance.corrections.columns.before', { defaultValue: '変更前' })}
                      </p>
                      <ChangeFieldRows value={r.old_value} />
                    </div>
                  ) : null}
                  {r.new_value != null ? (
                    <div>
                      <p className="text-[11px] font-medium text-efs-text-muted">
                        {t('efs.governance.corrections.columns.after', { defaultValue: '変更後' })}
                      </p>
                      <ChangeFieldRows value={r.new_value} />
                    </div>
                  ) : null}
                </>
              ) : showRowTechnicalDetails ? (
                <ChangeFieldRows value={r.requested_change} />
              ) : null}
              {!hasFieldChanges && !showRowTechnicalDetails && hasAnyChange ? (
                <span className="text-xs text-efs-text-muted">
                  {t('efs.governance.corrections.fields.safe_summary_unavailable')}
                </span>
              ) : null}
              {showRowTechnicalDetails && hasAnyChange ? (
                <details>
                  <summary className="cursor-pointer text-[11px] text-efs-text-muted hover:text-efs-text-primary">
                    {t('efs.issues.target.technical_details', { ns: 'issues' })}
                  </summary>
                  <pre className="mt-0.5 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded bg-efs-bg-subtle p-1.5 text-[11px] text-efs-text-muted">
                    {JSON.stringify(
                      {
                        requested_change: r.requested_change,
                        old_value: r.old_value,
                        new_value: r.new_value,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </details>
              ) : !hasFieldChanges && !hasAnyChange ? (
                <span className="text-xs text-efs-text-muted">-</span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'created_at',
        header: t('efs.governance.corrections.columns.created_at', { defaultValue: '作成日時' }),
        cell: (r) => (
          <span className="whitespace-nowrap text-xs text-efs-text-muted">
            {formatDateTime(r.created_at)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('efs.governance.corrections.columns.actions', { defaultValue: '操作' }),
        align: 'right',
        cell: (r) => {
          if (!canReviewCorrection(user, r.target_entity_type)) return null;
          // Row-specific pending: only the row whose id matches the in-flight
          // mutation's `variables` disables/spins — every other row's actions
          // stay fully interactive (spec-13 §8.8 round 2 B).
          const rowPending =
            (approve.isPending && approve.variables === r.id) ||
            (reject.isPending && reject.variables === r.id) ||
            (apply.isPending && apply.variables === r.id);
          if (r.status === 'requested') {
            return (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => approve.mutate(r.id)}
                  disabled={rowPending}
                >
                  {rowPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  {t('efs.governance.corrections.actions.approve', { defaultValue: '承認' })}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => reject.mutate(r.id)}
                  disabled={rowPending}
                >
                  {t('efs.governance.corrections.actions.reject', { defaultValue: '却下' })}
                </Button>
              </div>
            );
          }
          if (r.status === 'approved') {
            return (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => apply.mutate(r.id)} disabled={rowPending}>
                  {rowPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  {t('efs.governance.corrections.actions.apply', { defaultValue: '適用' })}
                </Button>
              </div>
            );
          }
          return null;
        },
      },
    ],
    [t, user, approve, reject, apply],
  );

  const filters: Array<CorrectionStatus | 'all'> = ['all', ...correctionStatusValues];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.governance.corrections.title', { defaultValue: '修正依頼' })}
        subtitle={t('efs.governance.corrections.subtitle', {
          defaultValue: 'ロック済みデータの修正依頼と承認',
        })}
        count={rows.length}
        actions={
          canRequest ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              {t('efs.governance.corrections.actions.create', { defaultValue: '依頼する' })}
            </Button>
          ) : null
        }
      />

      {actionError ? (
        <div className="space-y-1">
          <p className="text-sm font-medium text-efs-text-primary">
            {t('efs.governance.corrections.error.action_title', {
              defaultValue: '操作できませんでした',
            })}
          </p>
          <LocalizedApiError
            error={actionError}
            showTechnicalDetails={showActionTechnicalDetails}
          />
        </div>
      ) : null}

      {/* Status filter */}
      <div className="inline-flex flex-wrap gap-0.5 rounded-md border border-efs-border bg-efs-bg-panel p-0.5">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatusFilter(f)}
            className={cn(
              'rounded-sm px-3 py-1.5 text-sm font-medium',
              statusFilter === f
                ? 'bg-efs-bg-subtle text-efs-text-primary'
                : 'text-efs-text-muted hover:text-efs-text-primary',
            )}
          >
            {f === 'all'
              ? t('efs.governance.corrections.filter.all', { defaultValue: 'すべて' })
              : t(`efs.governance.corrections.status.${f}`, { defaultValue: f })}
          </button>
        ))}
      </div>

      {query.isError ? (
        <QueryFailureNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <MasterTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          isLoading={query.isLoading}
          emptyMessage={t('efs.governance.corrections.empty', {
            defaultValue: '修正依頼はありません。',
          })}
        />
      )}

      <CreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        isPending={create.isPending}
        error={create.error}
        showTechnicalDetails={showCreateTechnicalDetails}
      />
    </div>
  );
};

export default CorrectionRequestsPage;
